import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { X, Upload, Trash2, FileText, Image as ImageIcon, Camera } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Per-appointment attachments gallery + uploader.
 *
 * Admin and the assigned groomer can upload (server enforces RBAC).
 * Deletion is restricted to admin or the original uploader. Images get
 * thumbnail previews; PDFs render as a generic file tile. An optional
 * caption ("Before", "After", "Left side", etc.) is captured alongside
 * each file to aid later navigation in the chart view.
 *
 * Props:
 *   - appointmentId: string
 *   - currentUserId: string  — used to show/hide Trash icon on rows
 *   - userRole:      'admin' | 'groomer'
 *   - onClose:       () => void
 */
export default function AppointmentAttachmentsModal({
  appointmentId, currentUserId, userRole, onClose,
}) {
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState('');
  const fileInputRef = useRef(null);

  const authHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/appointments/${appointmentId}/attachments`,
        authHeader()
      );
      setAttachments(res.data || []);
    } catch (e) {
      toast.error('Failed to load attachments');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [appointmentId, authHeader, onClose]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);

    const form = new FormData();
    form.append('file', file);
    if (label.trim()) form.append('label', label.trim());

    try {
      const res = await axios.post(
        `${API_URL}/appointments/${appointmentId}/attachments`,
        form,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setAttachments((prev) => [res.data, ...prev]);
      setLabel('');
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm('Delete this attachment? This cannot be undone.')) return;
    try {
      await axios.delete(
        `${API_URL}/appointments/${appointmentId}/attachments/${att.id}`,
        authHeader()
      );
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
      toast.success('Attachment deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const canDelete = (att) => userRole === 'admin' || att.uploaded_by === currentUserId;

  const humanSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold flex items-center space-x-2">
            <Camera className="text-primary-600" size={22} />
            <span>Appointment Photos & Files</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Upload row */}
        <div className="p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              className="input flex-1"
              placeholder="Caption (optional) — e.g. Before, After, Left side"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={200}
              disabled={uploading}
            />
            <label
              className={`btn ${uploading ? 'btn-secondary' : 'btn-primary'} flex items-center space-x-2 cursor-pointer`}
            >
              <Upload size={16} />
              <span>{uploading ? 'Uploading…' : 'Upload photo / file'}</span>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
                accept="image/*,application/pdf"
              />
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-2">Max 10 MB. Images (jpg/png/webp/heic) or PDF.</p>
        </div>

        {/* Gallery */}
        <div className="p-5">
          {loading ? (
            <div className="py-12 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : attachments.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <ImageIcon size={48} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No photos or files yet. Upload the first one above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {attachments.map((att) => (
                <AttachmentTile
                  key={att.id}
                  att={att}
                  humanSize={humanSize}
                  canDelete={canDelete(att)}
                  onDelete={() => handleDelete(att)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}

function AttachmentTile({ att, humanSize, canDelete, onDelete }) {
  const isImage = typeof att.mime_type === 'string' && att.mime_type.startsWith('image/');
  const uploader = [att.uploader_first_name, att.uploader_last_name].filter(Boolean).join(' ');

  return (
    <div className="relative border border-gray-200 rounded overflow-hidden bg-white group">
      {isImage ? (
        <a href={att.file_url} target="_blank" rel="noopener noreferrer" className="block aspect-square bg-gray-100">
          <img
            src={att.file_url}
            alt={att.label || att.filename}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </a>
      ) : (
        <a
          href={att.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="aspect-square bg-gray-100 flex flex-col items-center justify-center p-3"
          title={att.filename}
        >
          <FileText size={36} className="text-primary-600 mb-2" />
          <span className="text-xs text-gray-700 truncate max-w-full">{att.filename}</span>
        </a>
      )}

      <div className="p-2 text-xs">
        {att.label && <div className="font-medium text-gray-800 truncate">{att.label}</div>}
        <div className="text-gray-500 truncate">
          {humanSize(att.byte_size)}
          {uploader && <span> · {uploader}</span>}
        </div>
      </div>

      {canDelete && (
        <button
          onClick={onDelete}
          className="absolute top-1 right-1 p-1 bg-white bg-opacity-90 rounded shadow text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Delete"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}
